import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DotsGrid } from "@untitledui/icons";
import type { CSSProperties } from "react";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { cx } from "@/utils/cx";

export interface SortableColumnItemProps {
	/** The column ID for sortable context */
	columnId: string;
	/** The display text for the column header */
	headerText: string;
	/** Whether the column is currently visible */
	isVisible: boolean;
	/** Callback when visibility is toggled */
	onToggleVisibility: (isChecked: boolean) => void;
	/**
	 * Disables drag reordering only (hide handle, `useSortable` disabled).
	 * Prefer filtering such columns out of the list; kept for edge cases.
	 */
	sortableDisabled?: boolean;
	/**
	 * Disables the visibility checkbox only (e.g. row-header / primary column).
	 * Drag remains enabled when false and sortableDisabled is false.
	 */
	isCheckboxDisabled?: boolean;
	/** Optional test id for the visibility checkbox. */
	visibilityToggleTestId?: string;
}

/**
 * SortableColumnItem is a draggable checkbox item for the View Options popover.
 * Allows reordering columns via drag and drop.
 *
 * @see Figma DropdownMenu Draggable menu item — 4962:186000
 */
export function SortableColumnItem({
	columnId,
	headerText,
	isVisible,
	onToggleVisibility,
	sortableDisabled = false,
	isCheckboxDisabled = false,
	visibilityToggleTestId,
}: SortableColumnItemProps) {
	const {
		attributes,
		isDragging,
		listeners,
		setNodeRef,
		transform,
		transition,
	} = useSortable({
		id: columnId,
		disabled: sortableDisabled,
	});

	const style: CSSProperties = {
		transform: CSS.Translate.toString(transform),
		transition: isDragging ? undefined : transition,
		opacity: isDragging ? 0.8 : 1,
		zIndex: isDragging ? 10 : 0,
		position: "relative",
	};

	const dimmed = sortableDisabled || isCheckboxDisabled;
	const showSelectedSurface = isVisible || isDragging;

	return (
		<div
			ref={setNodeRef}
			style={style}
			className={cx(
				"flex w-full items-start gap-(--spacing-none,0px)",
				dimmed && "opacity-60",
			)}
		>
			{sortableDisabled ? (
				<div
					className="w-[calc(var(--spacing-md,8px)+20px)] shrink-0"
					aria-hidden
				/>
			) : (
				<button
					type="button"
					className="flex shrink-0 cursor-grab touch-none items-center justify-center rounded-md py-(--spacing-md,8px) pl-(--spacing-md,8px) pr-(--spacing-none,0px) text-fg-quaternary hover:text-fg-secondary active:cursor-grabbing"
					aria-label={`Reorder ${headerText}`}
					{...attributes}
					{...listeners}
				>
					<DotsGrid className="size-5 shrink-0" aria-hidden />
				</button>
			)}
			{/* Figma MenuItem Content — selected: alpha-accent-2, px-lg py-md */}
			<div
				className={cx(
					"flex min-h-[38px] min-w-px flex-1 items-center overflow-clip rounded-md px-(--spacing-lg,12px) py-(--spacing-md,8px)",
					showSelectedSurface && "bg-[rgba(181,71,8,0.02)]",
				)}
			>
				<Checkbox
					data-testid={visibilityToggleTestId}
					isSelected={isVisible}
					onChange={onToggleVisibility}
					label={headerText}
					isDisabled={isCheckboxDisabled}
					className={cx(
						"w-full items-center gap-(--spacing-md,8px) [&_.relative]:mt-0 [&_p]:font-semibold",
						isVisible ? "[&_p]:text-primary" : "[&_p]:text-secondary",
					)}
				/>
			</div>
		</div>
	);
}
